'use client';

import { useEffect, useRef } from 'react';

import { useNotificationCounts } from '@/hooks/useNotificationCounts';
import { usePathname } from '@/i18n/navigation';
import { useGlobalData } from '@/providers/GlobalDataProvider';

/**
 * Hook to refresh notification counts when navigating between pages
 * Only refreshes if user is logged in
 */
export function useNotificationRefresh() {
  const pathname = usePathname();
  const user = useGlobalData('user');
  const { refreshCounts } = useNotificationCounts();
  const previousPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    // Only refresh if user is logged in
    if (!user) {
      previousPathnameRef.current = pathname;
      return;
    }

    // Skip initial mount (when previousPathname is null)
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = pathname;
      return;
    }

    // Only refresh if pathname actually changed
    if (previousPathnameRef.current !== pathname) {
      previousPathnameRef.current = pathname;
      // Refresh notification counts when navigating to a new page
      void refreshCounts();
    }
  }, [pathname, user, refreshCounts]);
}
